import PageIntroBand from 'components/PageIntroBand';
import { FlowButton } from 'components/ui/flow-button';
import HeroBackground from 'components/HeroBackground';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  FileCheck2,
  FileText,
  Gauge,
  Loader2,
  Route,
  ShieldCheck,
  Users,
  Workflow,
  XCircle,
} from 'lucide-react';
import { useReveal } from 'hooks/useReveal';
import { useSiteContent } from 'hooks/useSiteContent';
import { extractCarrierFlowReference, submitCompanyApplication } from 'lib/sitePublicApi';
import ConsultationNextSteps from 'components/ConsultationNextSteps';
import HeroScrollRail from 'components/HeroScrollRail';
import SiteHeroCtas, {
  HERO_CTA_PRIMARY_CLASS,
  HERO_CTA_SECONDARY_CLASS,
  PAGE_HERO_BODY_TEXT_CLASS,
  PAGE_HERO_CONTAINER_CLASS,
  PAGE_HERO_CTA_ROW_CLASS,
  PAGE_HERO_EYEBROW_CLASS,
  PAGE_HERO_H1_CLASS,
} from 'components/SiteHeroCtas';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from 'components/ui/accordion';
import { splitSiteContentParagraphs } from 'lib/siteContentCms';
import { toast } from 'sonner';

const COMPANY_STORAGE_KEY = 'magellanCompanyIntakeState:site-v1';
const SUBMISSION_SESSION_TTL_MS = 30 * 60 * 1000;
const COMPANIES_HERO_SECTION_CLASS = 'relative flex min-h-[100svh] w-full items-center overflow-hidden md:h-screen md:min-h-[680px]';
const COMPANIES_HERO_CONTENT = {
  eyebrow: 'Company review for motor carriers',
  title: 'Use this when the package fit is not clear.',
  body: 'Tell us what your carrier needs to control, where the paperwork is breaking down, and whether Basic, Standard, Premium, training, or custom support is the right next step.',
  primaryCtaLabel: 'Start Company Review',
  primaryCtaPath: '#company-form',
  secondaryCtaLabel: 'View Paperwork Packages',
  secondaryCtaPath: '/paperwork',
};
const COMPANIES_MARQUEE_ITEMS = [
  'Company review',
  'Driver files',
  'Safety follow-up',
  'Training rhythm',
  'Recruiting pipeline',
  'TMS workflows',
  'Management visibility',
  'Operating control',
];

const HERO_SYSTEM_AREAS = [
  'Driver files',
  'HOS/ELD proof',
  'Maintenance records',
  'Audit files',
  'Premium freight documentation',
  'Package fit',
];

const FIT_ITEMS = [
  'You are not sure whether Basic, Standard, or Premium is the correct fit.',
  'Your driver files, HOS/ELD proof, maintenance records, accident records, or audit files are hard to review.',
  'The same paperwork problems keep coming back.',
  'The owner is still the backup system for too many records, renewals, reminders, and follow-ups.',
  'You have active audit, insurance, broker, shipper, customer, or internal pressure.',
  'You are preparing for high-value freight, reefer operations, pharma-style expectations, stricter shipper review, vendor review, or premium freight documentation.',
  'You need help deciding whether the next step is a package, targeted training, file cleanup, monthly support, or custom implementation.',
];

const OPERATING_LAYER_CARDS = [
  {
    title: 'Company size, fleet type, and operating model',
    icon: FileCheck2,
    copy: 'Company Review is designed to understand the pressure point before recommending the next step.',
  },
  {
    title: 'Current paperwork structure',
    icon: ClipboardCheck,
    copy: 'Review may consider how records are organized, owned, reviewed, and connected to daily work.',
  },
  {
    title: 'Driver qualification files and onboarding records',
    icon: ShieldCheck,
    copy: 'Driver files, onboarding records, and document-control gaps can show whether Basic, Standard, or custom support is needed.',
  },
  {
    title: 'Drug/alcohol and Clearinghouse controls',
    icon: Users,
    copy: 'The review may consider whether sensitive compliance controls are tracked clearly enough for the company to manage.',
  },
  {
    title: 'HOS/ELD records',
    icon: Route,
    copy: 'Supporting documents, edits, unassigned miles, and log-review routines may point toward training, Standard, or file cleanup.',
  },
  {
    title: 'Maintenance and equipment proof',
    icon: FileText,
    copy: 'Maintenance, DVIR, PM, annual inspection, repair, and OOS proof may show whether records are connected to unit files.',
  },
  {
    title: 'Accident, crash, incident, and roadside inspection records',
    icon: Workflow,
    copy: 'The review may consider whether follow-up, evidence, and corrective action are organized enough for pressure.',
  },
  {
    title: 'Package, training, custom service, or monthly support fit',
    icon: Gauge,
    copy: 'The goal is to decide whether the issue should be handled through Basic, Standard, Premium, targeted training, custom service, or monthly support.',
  },
];

const SMALL_GAPS = [
  'You only need starter cleanup and baseline file discipline.',
  'You already know Basic is the right starting point.',
  'You already know Standard is the right operating paperwork system for your company.',
  'You are comparing packages but do not have an active audit, urgent dispute, premium freight issue, or unclear scope.',
];

const PROCESS_STEPS = [
  {
    title: 'Magellan reviews the information provided.',
    copy: 'Submitting the form does not automatically create a consulting engagement, legal engagement, or service obligation.',
  },
  {
    title: 'Magellan may follow up if there appears to be a fit.',
    copy: 'If there appears to be a fit, Magellan may follow up with questions or next steps.',
  },
  {
    title: 'Magellan may recommend the next path.',
    copy: 'Magellan may recommend a paperwork package, targeted training, custom review, file cleanup, monthly support, or no immediate service.',
  },
  {
    title: 'Legal matters go to qualified counsel.',
    copy: 'If the situation requires legal advice, agency representation, citation defense, court representation, civil penalty response, safety rating appeal, employment-law guidance, contract interpretation, or state-specific legal analysis, the company should contact qualified legal counsel.',
  },
  {
    title: 'Paid work must be accepted separately.',
    copy: 'Any paid service, package purchase, implementation scope, or support arrangement must be accepted separately under the applicable terms.',
  },
];

const HONESTY_PROMPTS = [
  'Company name.',
  'Contact name and role.',
  'Email and phone number.',
  'Website, if available.',
  'USDOT / MC number, if you want Magellan to review public-facing carrier context.',
  'Fleet size.',
  'Equipment type.',
  'Operation type.',
  'Main paperwork or operating problem.',
  'Whether there is audit, insurance, broker, shipper, customer, or internal pressure.',
  'Areas needing attention.',
  'What you already tried.',
  'How urgent the issue is.',
  'Whether you are interested in Basic, Standard, Premium, training, or custom support.',
  'Any additional context that helps explain the situation.',
];

const NOT_THIS_ITEMS = [
  'Company Review is not legal advice, agency representation, citation defense, court representation, civil penalty response, safety rating appeal, employment-law guidance, contract interpretation, or state-specific legal analysis.',
  'Company Review does not guarantee audit results, DataQs corrections, CSA/SMS improvement, insurance approval, broker acceptance, shipper approval, customer approval, citation dismissal, or enforcement outcomes.',
  'Company Review is a practical routing step. Its purpose is to help identify whether the company should move toward a package, training, custom support, or another professional path.',
];

const EXPECTATION_ITEMS = [
  'Company size, fleet type, and operating model.',
  'Current paperwork structure.',
  'Driver qualification files and onboarding records.',
  'Drug/alcohol and Clearinghouse controls.',
  'HOS/ELD supporting documents, edits, unassigned miles, and log-review routines.',
  'Maintenance, DVIR, PM, annual inspection, repair, and OOS proof.',
  'Accident, crash, incident, and roadside inspection records.',
  'CSA/DataQs review, evidence packets, root cause, and corrective action.',
  'Authority, MCS-150, credentials, portal access, insurance/BOC-3, and renewal calendars.',
  'Training, SOPs, acknowledgements, trackers, and management review.',
  'Premium freight documentation needs where applicable.',
  'Whether the issue should be handled through Basic, Standard, Premium, targeted training, custom service, or monthly support.',
];

const SUPPORT_POINTS = [
  'This page is for trucking companies requesting review before choosing a package, training path, or support scope.',
  'If you already know which package fits, go directly to the Paperwork Packages page.',
  'If you are applying to work with Magellan as an applicant, contractor, assistant, sales-support candidate, documentation-support candidate, operations-support candidate, recruiting-support candidate, or training-support candidate, use the Work With Us page instead.',
];

const FLEET_SIZE_OPTIONS = [
  ['5', '1-5 trucks'],
  ['15', '6-15 trucks'],
  ['50', '16-50 trucks'],
  ['100', '51-100 trucks'],
  ['101', '100+ trucks'],
];

const OPERATION_TYPE_OPTIONS = [
  ['Dry van', 'Dry van'],
  ['Reefer', 'Reefer'],
  ['Flatbed', 'Flatbed'],
  ['Intermodal', 'Intermodal'],
  ['Car hauling', 'Car hauling'],
  ['Local / regional', 'Local / regional'],
  ['OTR', 'OTR'],
  ['Mixed operation', 'Mixed operation'],
  ['Other', 'Other'],
];

const ATTENTION_AREA_OPTIONS = [
  'Driver files',
  'Compliance folders',
  'DOT audit readiness',
  'Safety meetings',
  'Incident review',
  'Inspection follow-up / DataQs',
  'Driver onboarding',
  'Training',
  'Recruiting',
  'TMS workflows',
  'SOPs',
  'Dashboards / management visibility',
  'Other',
];

const URGENCY_OPTIONS = [
  ['Immediately', 'Immediately'],
  ['Within 30 days', 'Within 30 days'],
  ['Within 60-90 days', 'Within 60-90 days'],
  ['Planning ahead', 'Planning ahead'],
  ['Not sure yet', 'Not sure yet'],
];

const FAQS = [
  ['Is this only for large trucking companies?', 'No. Small and mid-sized carriers often need structure earlier because one missing file, weak onboarding process, safety issue, insurance problem, failed audit, or undocumented follow-up can create serious damage. The right fit depends less on size and more on whether the company is ready to take the work seriously.'],
  ['What happens after we submit the Company Review?', 'We review the information and determine whether Magellan appears to be a fit for your current needs. If there is a fit, the next step may be a review conversation, additional questions, or a more detailed operations review.'],
  ['Do we need to know exactly what service we need?', 'No. Many companies know something is not working, but they are not sure whether the issue is paperwork, training, safety follow-up, software usage, recruiting, management visibility, or ownership. The Company Review helps identify where the pressure is coming from.'],
  ['Can Magellan help build the actual systems?', 'Yes. Magellan can help build practical systems around driver files, compliance records, safety meetings, incident review, onboarding, training, recruiting, SOPs, TMS workflows, dashboards, and management review. The exact scope depends on what the company needs.'],
  ['Do you provide legal or regulatory advice?', 'No. Magellan provides operational documentation, workflow structure, training support, and implementation systems. Legal, regulatory, insurance, and compliance decisions should be reviewed with qualified professionals when needed.'],
  ['Is this a one-time cleanup or ongoing support?', 'It can be either, depending on the company\'s needs. Some companies need a focused cleanup or system build. Others need ongoing implementation support, review rhythm, training, or management visibility. The first step is understanding what the company actually needs.'],
];

function sanitizeText(value) {
  return String(value || '').trim();
}

function sanitizeStoredSubmission(value) {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const expiresAt = Number(value.expiresAt);
  if (Number.isFinite(expiresAt) && expiresAt < Date.now()) {
    return null;
  }

  const referenceId = sanitizeText(value.referenceId);
  if (!referenceId) {
    return null;
  }

  return {
    referenceId,
    companyName: sanitizeText(value.companyName),
    contactName: sanitizeText(value.contactName),
    email: sanitizeText(value.email),
    phone: sanitizeText(value.phone),
    companyAddress: sanitizeText(value.companyAddress),
    companyZipCode: sanitizeText(value.companyZipCode),
    dotMc: sanitizeText(value.dotMc),
    dotNumber: sanitizeText(value.dotNumber),
    mcNumber: sanitizeText(value.mcNumber),
  };
}

function readStoredSubmission() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(COMPANY_STORAGE_KEY);
    return raw ? sanitizeStoredSubmission(JSON.parse(raw)) : null;
  } catch (error) {
    return null;
  }
}

function writeStoredSubmission(value) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const referenceId = sanitizeText(value?.referenceId);
    if (!referenceId) {
      window.sessionStorage.removeItem(COMPANY_STORAGE_KEY);
      return;
    }

    window.sessionStorage.setItem(COMPANY_STORAGE_KEY, JSON.stringify({
      referenceId: referenceId.slice(0, 128),
      companyName: sanitizeText(value?.companyName).slice(0, 200),
      contactName: sanitizeText(value?.contactName).slice(0, 200),
      email: sanitizeText(value?.email).slice(0, 320),
      phone: sanitizeText(value?.phone).slice(0, 80),
      companyAddress: sanitizeText(value?.companyAddress).slice(0, 1000),
      companyZipCode: sanitizeText(value?.companyZipCode).slice(0, 40),
      dotMc: sanitizeText(value?.dotMc).slice(0, 80),
      dotNumber: sanitizeText(value?.dotNumber).slice(0, 80),
      mcNumber: sanitizeText(value?.mcNumber).slice(0, 80),
      expiresAt: Date.now() + SUBMISSION_SESSION_TTL_MS,
    }));
  } catch (error) {
    console.error('Failed to persist company submission:', error);
  }
}

function Field({ label, value, onChange, placeholder, type = 'text', required = false, testId, options }) {
  const fieldId = testId || `company-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div>
      <label htmlFor={fieldId} className="text-xs uppercase tracking-wider text-copy/45 font-body mb-2 block">{label}{required ? ' *' : ''}</label>
      {options ? (
        <select
          id={fieldId}
          data-testid={testId}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full rounded-none bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors font-body"
        >
          <option value="">Select one</option>
          {options.map(([optionValue, optionLabel]) => (
            <option key={optionValue} value={optionValue}>{optionLabel}</option>
          ))}
        </select>
      ) : (
        <input
          id={fieldId}
          data-testid={testId}
          type={type}
          required={required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-copy/22 font-body"
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

function TextArea({ label, value, onChange, placeholder, required = false, rows = 4, testId }) {
  const fieldId = testId || `company-field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div>
      <label htmlFor={fieldId} className="text-xs uppercase tracking-wider text-copy/45 font-body mb-2 block">{label}{required ? ' *' : ''}</label>
      <textarea
        id={fieldId}
        data-testid={testId}
        rows={rows}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border-b border-ink/20 text-ink py-3 px-0 focus:border-gold focus:outline-none transition-colors placeholder:text-copy/22 font-body resize-none"
        placeholder={placeholder}
      />
    </div>
  );
}

function CheckboxGroup({ label, options, value, onChange }) {
  const selected = Array.isArray(value) ? value : [];
  const toggle = (option) => {
    onChange(selected.includes(option)
      ? selected.filter((item) => item !== option)
      : [...selected, option]);
  };

  return (
    <fieldset>
      <legend className="text-xs uppercase tracking-wider text-copy/45 font-body mb-4 block">{label} *</legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((option) => {
          const fieldId = `company-area-${option.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
          const checked = selected.includes(option);
          return (
            <label
              key={option}
              htmlFor={fieldId}
              className={`flex items-center gap-3 border px-4 py-3 text-sm font-body transition-colors ${checked ? 'border-gold/55 bg-gold/[0.08] text-ink' : 'border-ink/10 bg-ink/[0.025] text-copy/62 hover:border-ink/25'}`}
            >
              <input
                id={fieldId}
                type="checkbox"
                checked={checked}
                onChange={() => toggle(option)}
                className="h-4 w-4 rounded border-ink/20 bg-transparent accent-gold"
              />
              {option}
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

function FormSection({ eyebrow, title, helper, children }) {
  return (
    <div className="border border-ink/8 bg-canvas p-6 md:p-8 shadow-[0_20px_90px_rgba(0,0,0,0.18)]">
      <p className="text-[10px] uppercase tracking-[0.28em] text-accent-ink/75 font-body mb-4">{eyebrow}</p>
      <h3 className="font-display text-2xl md:text-3xl text-ink mb-4">{title}</h3>
      {helper ? <p className="font-body text-copy/48 text-sm leading-relaxed mb-8 max-w-3xl">{helper}</p> : null}
      <div className="space-y-8">{children}</div>
    </div>
  );
}

function SectionHeader({ eyebrow, title, copy, className = '' }) {
  return (
    <div className={className}>
      <p className="reveal text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">{eyebrow}</p>
      <h2 className="reveal reveal-delay-1 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.02] text-ink mb-6">{title}</h2>
      {copy ? <p className="reveal reveal-delay-2 font-body text-copy/52 leading-relaxed max-w-3xl">{copy}</p> : null}
    </div>
  );
}

function HeroTitle({ title }) {
  const words = sanitizeText(title).split(/\s+/).filter(Boolean);
  const goldStartIndex = Math.max(0, words.length - 3);

  return words.map((word, index) => (
    <span key={`${word}-${index}`} aria-hidden="true" className="mr-3 inline-block overflow-hidden align-bottom sm:mr-4">
      <span className={`inline-block ${index >= goldStartIndex ? 'italic shimmer-text' : ''}`}>
        {word}
      </span>
    </span>
  ));
}

function CheckList({ items, icon: Icon = CheckCircle2, className = '' }) {
  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${className}`}>
      {items.map((item) => (
        <div key={item} className="flex items-start gap-3 border border-ink/8 bg-ink/[0.025] p-4">
          <Icon className="w-4 h-4 text-accent-ink mt-1 flex-shrink-0" strokeWidth={1.7} />
          <p className="font-body text-sm leading-relaxed text-copy/62">{item}</p>
        </div>
      ))}
    </div>
  );
}

function CompaniesIntroBand() {
  return (
    <PageIntroBand
      testId="companies-page-intro"
      label="Company review"
      title="Find your starting point."
      backgroundText="COMPANIES"
      paragraphs={[
        "Request a review to discuss paperwork, training or support. Already know your package? Go directly to Paperwork Packages.",
        "This page is for trucking companies. Individuals applying for work should use Work With Us.",
      ]}
    />
  );
}

export default function CompaniesPage() {
  const ref = useReveal();
  const navigate = useNavigate();
  const cmsHero = useSiteContent('companiesHero');
  const hero = COMPANIES_HERO_CONTENT;
  const heroParagraphs = splitSiteContentParagraphs(hero.body);
  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    contactTitle: '',
    email: '',
    phone: '',
    website: '',
    companyAddress: '',
    companyZipCode: '',
    fleetSize: '',
    operationType: '',
    areasNeedAttention: [],
    painPoints: '',
    currentSystems: '',
    urgency: '',
    additionalNotes: '',
    dotMc: '',
    dotNumber: '',
    mcNumber: '',
    freightType: '',
    interest: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const stored = readStoredSubmission();
    if (!stored) {
      return;
    }

    setSubmitted(true);
    setSubmission(stored);
  }, []);

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.areasNeedAttention.length) {
      toast.error('Select at least one area that needs attention.');
      return;
    }
    if (!sanitizeText(form.dotNumber) && !sanitizeText(form.mcNumber) && !sanitizeText(form.dotMc)) {
      toast.error('Enter a DOT number or MC number.');
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await submitCompanyApplication({
        ...form,
        freightType: form.operationType,
        interest: form.areasNeedAttention.join(', '),
      });
      if (result.success) {
        const savedSubmission = {
          referenceId: extractCarrierFlowReference(result.data),
          companyName: form.companyName,
          contactName: form.contactName,
          email: form.email,
          phone: form.phone,
          companyAddress: form.companyAddress,
          companyZipCode: form.companyZipCode,
          dotMc: form.dotMc,
          dotNumber: form.dotNumber,
          mcNumber: form.mcNumber,
        };

        setSubmitted(true);
        setSubmission(savedSubmission);
        writeStoredSubmission(savedSubmission);
        toast.success('Company Review received');
        navigate('/companies/application-submitted');
      } else {
        toast.error('Failed to submit review request. Please try again.');
        console.error('Submission error:', result.error);
      }
    } catch (error) {
      toast.error('An unexpected error occurred.');
      console.error('Unexpected error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={ref} className="min-h-screen bg-canvas">
      <section data-theme-surface="dark" data-testid="companies-hero" className={`${COMPANIES_HERO_SECTION_CLASS} site-image-hero`}>
        <HeroBackground imageUrl={cmsHero.imageUrl} imageAlt={cmsHero.imageAlt} />
        <div className="absolute inset-0 grain-overlay" />
        <div data-hero-text-parallax="true" className={PAGE_HERO_CONTAINER_CLASS}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(360px,0.7fr)] gap-10 lg:gap-14 items-center">
            <div className="min-w-0 w-full max-w-[342px] sm:max-w-4xl">
              <p className={PAGE_HERO_EYEBROW_CLASS}>{hero.eyebrow}</p>
              <h1 aria-label={hero.title} className={PAGE_HERO_H1_CLASS} style={{ letterSpacing: 0 }}><HeroTitle title={hero.title} /></h1>
              <div className={`${PAGE_HERO_BODY_TEXT_CLASS} space-y-4`}>
                {heroParagraphs.slice(0, 3).map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
              <SiteHeroCtas
                content={hero}
                className={PAGE_HERO_CTA_ROW_CLASS}
                primaryClassName={HERO_CTA_PRIMARY_CLASS}
                secondaryClassName={HERO_CTA_SECONDARY_CLASS}
                tertiaryClassName={HERO_CTA_SECONDARY_CLASS}
              />
              <p className="reveal reveal-delay-3 mt-5 max-w-xl font-body text-xs leading-relaxed text-copy/42">
                This page is for trucking companies requesting review before choosing a package, training path, or support scope. If you are applying to work with Magellan, use the <Link to="/work-with-us" className="text-accent-ink hover:text-accent-ink-hover">Work With Us page</Link>.
              </p>
            </div>

            <div className="reveal reveal-delay-2 hidden lg:block">
              <div className="border border-gold/22 bg-canvas/58 backdrop-blur-md p-5 xl:p-6">
                <p className="text-[10px] uppercase tracking-[0.28em] text-accent-ink font-body mb-5">System areas reviewed</p>
                <div className="space-y-3">
                  {HERO_SYSTEM_AREAS.map((item) => (
                    <div key={item} className="flex items-start gap-3 border-b border-ink/8 pb-3 last:border-b-0 last:pb-0">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                      <p className="font-body text-sm leading-relaxed text-copy/68">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <HeroScrollRail targetId="company-form" />
      </section>

      <div className="topic-rail overflow-hidden border-y border-ink/10 bg-surface">
        <div className="marquee-container">
          <div className="marquee-track">
            {[...COMPANIES_MARQUEE_ITEMS, ...COMPANIES_MARQUEE_ITEMS].map((item, index) => (
              <span key={`${item}-${index}`} className="inline-flex items-center gap-3 px-9 font-body text-[9px] uppercase tracking-[0.44em] text-copy/24 whitespace-nowrap">
                {item}
                <span className="h-1 w-1 rotate-45 bg-gold/45" />
              </span>
            ))}
          </div>
        </div>
      </div>

      <CompaniesIntroBand />

      <section className="py-16 md:py-20 bg-surface border-y border-ink/6">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <SectionHeader
            eyebrow="When to use Company Review"
            title="Company Review is not required for every buyer."
            copy="It is useful when the situation is unclear, active, urgent, or too specific for a blind package purchase."
          />
          <div className="reveal reveal-delay-2 space-y-5 font-body text-copy/56 leading-relaxed">
            <p>Use Company Review if:</p>
            <p>You are not sure whether Basic, Standard, or Premium is the correct fit.</p>
            <p className="text-copy/75">You need help deciding whether the next step is a package, targeted training, file cleanup, monthly support, or custom implementation.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="When to use Company Review"
            title="Use Company Review if:"
            copy="The review is meant for unclear, active, urgent, or company-specific situations."
            className="max-w-4xl mb-10"
          />
          <CheckList items={FIT_ITEMS} />
          <div className="reveal reveal-delay-2 mt-8 border border-gold/20 bg-gold/[0.045] p-6 md:p-8">
            <p className="font-body text-copy/70 leading-relaxed">We are not looking for companies that only want a document dropped into a folder. We work with companies that want the system behind the document.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-surface">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="What Magellan reviews"
            title="Company Review is designed to understand the pressure point before recommending the next step."
            copy="The review may consider:"
            className="max-w-4xl mb-10"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {OPERATING_LAYER_CARDS.map(({ title, copy, icon: Icon }) => (
              <div key={title} className="reveal border border-ink/8 bg-canvas p-5 md:p-6">
                <Icon className="w-7 h-7 text-accent-ink mb-6" strokeWidth={1.4} />
                <h3 className="font-display text-xl text-ink mb-3 leading-tight">{title}</h3>
                <p className="font-body text-sm leading-relaxed text-copy/48">{copy}</p>
              </div>
            ))}
          </div>
          <p className="reveal mt-8 font-body text-copy/58 leading-relaxed max-w-3xl">
            The review may also consider CSA/DataQs review, evidence packets, root cause, corrective action, authority, MCS-150, credentials, portal access, insurance/BOC-3, renewal calendars, training, SOPs, acknowledgements, trackers, management review, and premium freight documentation needs where applicable.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-[0.88fr_1.12fr] gap-12 lg:gap-16 items-start">
          <SectionHeader
            eyebrow="When you do not need Company Review"
            title="You may not need review before buying if the situation is simple."
            copy="Go directly to Paperwork Packages if:"
          />
          <div className="reveal reveal-delay-2 border-l border-gold/30 pl-6 md:pl-8">
            <div className="space-y-4">
              {SMALL_GAPS.map((item, index) => (
                <div key={item} className="grid grid-cols-[42px_1fr] gap-4 items-start">
                  <span className="font-display text-accent-ink/70 text-2xl leading-none">{String(index + 1).padStart(2, '0')}</span>
                  <p className="font-body text-copy/64 leading-relaxed border-b border-ink/8 pb-4">{item}</p>
                </div>
              ))}
            </div>
            <p className="mt-8 font-body text-copy/54 leading-relaxed">
              View Paperwork Packages
            </p>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="py-16 md:py-20 bg-surface">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="What happens after you submit"
            title="Submitting the form does not automatically create a consulting engagement, legal engagement, or service obligation."
            className="max-w-4xl mb-12"
          />
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
            {PROCESS_STEPS.map((step, index) => (
              <div key={step.title} className="reveal relative border-l border-gold/24 lg:border-l-0 lg:border-t p-6">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold/40 text-accent-ink font-body text-xs mb-5 bg-canvas">{index + 1}</span>
                <h3 className="font-display text-xl text-ink mb-3">{step.title}</h3>
                <p className="font-body text-copy/48 text-sm leading-relaxed">{step.copy}</p>
                {index === 0 ? (
                  <a href="#company-form" className="mt-5 inline-flex items-center gap-2 text-accent-ink font-body text-xs uppercase tracking-[0.22em] hover:text-accent-ink-hover">
                    Start Company Review <ArrowRight className="w-4 h-4" />
                  </a>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="reveal border border-ink/8 bg-surface p-7 md:p-9">
            <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">What to include in the form</p>
            <h2 className="font-display text-3xl sm:text-4xl text-ink tracking-tight mb-6">The more specific the request is, the more useful the review will be.</h2>
            <p className="font-body text-copy/52 leading-relaxed mb-8">Include:</p>
            <div className="space-y-3">
              {HONESTY_PROMPTS.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-accent-ink mt-1" strokeWidth={1.7} />
                  <p className="font-body text-sm leading-relaxed text-copy/62">{item}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="reveal reveal-delay-1 border border-ink/8 bg-surface p-7 md:p-9">
            <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">What Company Review is not</p>
            <h2 className="font-display text-3xl sm:text-4xl text-ink tracking-tight mb-6">Company Review is a practical routing step.</h2>
            <p className="font-body text-copy/52 leading-relaxed mb-8">Its purpose is to help identify whether the company should move toward a package, training, custom support, or another professional path.</p>
            <div className="space-y-3">
              {NOT_THIS_ITEMS.map((item) => (
                <div key={item} className="flex items-start gap-3">
                  <XCircle className="w-4 h-4 text-accent-ink mt-1" strokeWidth={1.7} />
                  <p className="font-body text-sm leading-relaxed text-copy/62">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-surface">
        <div className="site-content-shell grid grid-cols-1 lg:grid-cols-[0.82fr_1.18fr] gap-12 items-start">
          <SectionHeader
            eyebrow="What Magellan reviews"
            title="The review may consider:"
            copy="Company Review is designed to understand the pressure point before recommending the next step."
          />
          <CheckList items={EXPECTATION_ITEMS} className="sm:grid-cols-2" />
        </div>
      </section>

      <section data-theme-surface="dark" className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-48" style={{ backgroundImage: 'url(/images/companies/company-road-truck.png)' }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/88 to-canvas/50" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="site-content-shell relative z-10 grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 items-start">
          <SectionHeader
            eyebrow="Important routing note"
            title="Choose the right page before submitting."
            copy="This page is for trucking companies requesting review before choosing a package, training path, or support scope."
          />
          <div className="reveal reveal-delay-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SUPPORT_POINTS.map((item) => (
              <div key={item} className="border border-ink/10 bg-canvas/58 backdrop-blur-sm p-4">
                <p className="font-body text-sm leading-relaxed text-copy/68">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="company-form" data-testid="companies-form-section" className="py-16 md:py-20 bg-surface">
        <div className="site-content-shell">
          <SectionHeader
            eyebrow="Company review"
            title="Start Company Review"
            copy="Tell us what your carrier needs to control, where the paperwork is breaking down, and whether Basic, Standard, Premium, training, or custom support is the right next step."
            className="max-w-4xl mb-10"
          />

          {submitted ? (
            <div>
              <div data-testid="companies-form-success" className="reveal text-center py-16 border border-gold/20 bg-canvas">
                <CheckCircle2 className="w-12 h-12 text-accent-ink mx-auto mb-6" />
                <h3 className="font-display text-2xl text-ink mb-4">Company Review Received</h3>
                <p className="font-body text-copy/50 max-w-2xl mx-auto">Thank you. Your Company Review has been received. If there appears to be a fit, we will review the information and follow up with next steps.</p>
              </div>
              {submission ? (
                <ConsultationNextSteps
                  sourcePage="companies"
                  storageKey="magellanCompanyConsultationFlow"
                  leadContext={submission}
                  introTitle="Reserve a paid fit review"
                  introBody="If you want to move directly into a paid review, continue into payment first and then open the booking calendar for the live meeting slot."
                  completionBody="We received your company review. A Magellan team member will review your information and respond within 24 hours."
                  singleStepFlow
                />
              ) : null}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="reveal reveal-delay-2 space-y-6" data-testid="companies-application-form">
              <FormSection
                eyebrow="Review form"
                title="Please complete the form with accurate information."
                helper="We use this to understand your company, your current operating pressure, and the areas where structure may be needed. Fields marked as required should be completed before submitting."
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Company name" required testId="companies-input-name" value={form.companyName} onChange={(value) => updateForm('companyName', value)} placeholder="Enter company name" />
                  <Field label="Your name" required testId="companies-input-contact" value={form.contactName} onChange={(value) => updateForm('contactName', value)} placeholder="Enter your full name" />
                  <Field label="Your role" required value={form.contactTitle} onChange={(value) => updateForm('contactTitle', value)} placeholder="Owner, manager, safety, dispatch, recruiting, operations, other" />
                  <Field label="Email address" required type="email" testId="companies-input-email" value={form.email} onChange={(value) => updateForm('email', value)} placeholder="Enter work email" />
                  <Field label="Phone number" required type="tel" testId="companies-input-phone" value={form.phone} onChange={(value) => updateForm('phone', value)} placeholder="Enter phone number" />
                  <Field label="Company website" testId="companies-input-website" value={form.website} onChange={(value) => updateForm('website', value)} placeholder="Enter website if available" />
                  <Field label="Company address" required testId="companies-input-address" value={form.companyAddress} onChange={(value) => updateForm('companyAddress', value)} placeholder="Street address, city, state" />
                  <Field label="Company ZIP code" required testId="companies-input-zip" value={form.companyZipCode} onChange={(value) => updateForm('companyZipCode', value)} placeholder="Enter ZIP code" />
                  <Field label="DOT Number" type="text" testId="companies-input-dot" value={form.dotNumber} onChange={(value) => updateForm('dotNumber', value)} placeholder="Enter DOT number" />
                  <Field label="MC Number" type="text" testId="companies-input-mc" value={form.mcNumber} onChange={(value) => updateForm('mcNumber', value)} placeholder="Enter MC number if available" />
                  <Field label="Fleet size" required testId="companies-input-fleet" value={form.fleetSize} onChange={(value) => updateForm('fleetSize', value)} options={FLEET_SIZE_OPTIONS} />
                  <Field label="Primary operation type" required testId="companies-input-freight" value={form.operationType} onChange={(value) => updateForm('operationType', value)} options={OPERATION_TYPE_OPTIONS} />
                </div>
                <CheckboxGroup label="What areas need attention?" options={ATTENTION_AREA_OPTIONS} value={form.areasNeedAttention} onChange={(value) => updateForm('areasNeedAttention', value)} />
                <TextArea label="What is the biggest problem right now?" required testId="companies-input-pain" value={form.painPoints} onChange={(value) => updateForm('painPoints', value)} placeholder="Tell us what is creating the most pressure inside the company." />
                <TextArea label="What have you already tried?" testId="companies-input-systems" value={form.currentSystems} onChange={(value) => updateForm('currentSystems', value)} placeholder="Software, templates, internal process, consultant, staff changes, other." />
                <Field label="How soon do you want to address this?" required testId="companies-input-urgency" value={form.urgency} onChange={(value) => updateForm('urgency', value)} options={URGENCY_OPTIONS} />
                <TextArea label="Anything else we should know?" testId="companies-input-notes" value={form.additionalNotes} onChange={(value) => updateForm('additionalNotes', value)} placeholder="Add context that would help us understand the company." />
              </FormSection>

              <FlowButton type="submit" disabled={isSubmitting} data-testid="companies-form-submit" className="inline-flex items-center justify-center gap-3 rounded-full   px-8 py-4 text-sm font-body font-semibold  transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? (
                  <>Submitting <Loader2 className="w-4 h-4 animate-spin" /></>
                ) : (
                  <>Submit Company Review </>
                )}
              </FlowButton>
            </form>
          )}
        </div>
      </section>

      <section className="py-14 md:py-16">
        <div className="site-content-frame border border-ink/8 bg-surface p-7 md:p-9">
          <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">What Company Review is not</p>
          <h2 className="font-display text-3xl text-ink tracking-tight mb-5">Company Review is not legal advice.</h2>
          <p className="font-body text-copy/54 leading-relaxed">
            Company Review is not legal advice, agency representation, citation defense, court representation, civil penalty response, safety rating appeal, employment-law guidance, contract interpretation, or state-specific legal analysis. Company Review does not guarantee audit results, DataQs corrections, CSA/SMS improvement, insurance approval, broker acceptance, shipper approval, customer approval, citation dismissal, or enforcement outcomes.
          </p>
        </div>
      </section>

      <section id="faq" data-testid="companies-faq" className="border-t border-ink/10 bg-canvas py-24 md:py-32">
        <div className="site-content-shell">
          <div className="mb-12 text-center">
            <p className="mb-4 font-body text-xs uppercase tracking-[0.3em] text-accent-ink">Questions</p>
            <h2 className="font-display text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">Company review questions.</h2>
          </div>
          <Accordion type="single" collapsible className="w-full border-t border-ink/10" data-testid="companies-faq-accordion">
            {FAQS.map(([question, answer], index) => (
              <AccordionItem key={question} value={`companies-faq-${index}`} data-testid={`companies-faq-item-${index}`} className="border-b border-ink/10">
                <AccordionTrigger className="group py-7 text-left hover:no-underline">
                  <div className="flex items-start gap-5">
                    <span className="font-display text-2xl text-tone-600 transition-colors group-hover:text-accent-ink">{String(index + 1).padStart(2, '0')}</span>
                    <span className="font-display text-xl text-ink transition-colors group-hover:text-accent-ink md:text-2xl">{question}</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-7 pl-[3.4rem] font-body text-base leading-8 text-tone-400">
                  {answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section data-theme-surface="dark" className="relative py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center opacity-42" style={{ backgroundImage: 'url(/images/companies/company-road-truck.png)' }} aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-canvas via-canvas/90 to-canvas/62" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="site-content-shell relative z-10 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent-ink font-body mb-4">Next step</p>
            <h2 className="font-display text-3xl sm:text-5xl text-ink tracking-tight leading-[1.02] max-w-3xl mb-6">Use this when the package fit is not clear.</h2>
            <p className="font-body text-copy/58 leading-relaxed max-w-3xl">
              Tell us what your carrier needs to control, where the paperwork is breaking down, and whether Basic, Standard, Premium, training, or custom support is the right next step.
            </p>
            <p className="mt-4 font-body text-copy/42 text-sm">For job applicants, contractors, and support candidates, please use the <Link to="/work-with-us" className="text-accent-ink hover:text-accent-ink-hover">Work With Us page</Link>.</p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
            <FlowButton as="a" href="#company-form" className={HERO_CTA_PRIMARY_CLASS}>Start Company Review </FlowButton>
            <FlowButton to="/paperwork" className={HERO_CTA_SECONDARY_CLASS}>View Paperwork Packages </FlowButton>
          </div>
        </div>
      </section>
    </div>
  );
}
